import { instance } from './ApiRequest';
import {
    GOVERNANCE_SUBMIT_FOR_APPROVAL, GOVERNANCE_GET_PENDING_REQUESTS, GOVERNANCE_APPROVE, GOVERNANCE_REJECT, AUDIT_LOG_SEARCH,
    ENTERPRISE_CONFIG_GET_ALL, ENTERPRISE_CONFIG_GET_BY_CATEGORY, ENTERPRISE_CONFIG_UPDATE, ENTERPRISE_CONFIG_IS_FEATURE_ENABLED, ENTERPRISE_CONFIG_GET_BRANDING, ENTERPRISE_CONFIG_GET_LANGUAGE,
    SYSTEM_HEALTH_GET_STATUS, SYSTEM_HEALTH_GET_DIAGNOSTICS, SYSTEM_HEALTH_GET_BACKUP_STATUS, SYSTEM_HEALTH_GET_RECENT_SECURITY_EVENTS,
    OPERATIONAL_DASHBOARD_GET_METRICS, COMPLIANCE_DASHBOARD_GET_METRICS,
    COMMUNICATION_GET_HEALTH, COMMUNICATION_TEST_EMAIL, COMMUNICATION_TEST_SMS, COMMUNICATION_GET_QUEUE_SUMMARY
} from 'constants/constants';

// Sprint 2.4: single wrapper module for the Sprint 2.2/2.3 backend surface that had zero frontend
// callers before this sprint (Governance, EnterpriseConfig, SystemHealth, OperationalDashboard,
// ComplianceDashboard) — kept as one module rather than five near-empty ones, consistent with the
// existing AuthService.js/AdminService.js convention of one service file per related API surface.
let _EnterpriseOpsService = {

    // --- Enterprise Configuration Framework + Feature Flag Framework (Sprint 2.3) ---
    async getAllConfig() {
        return await instance.get(ENTERPRISE_CONFIG_GET_ALL);
    },
    async getConfigByCategory(category) {
        return await instance.get(ENTERPRISE_CONFIG_GET_BY_CATEGORY + "?category=" + encodeURIComponent(category));
    },
    async updateConfig(model) {
        return await instance.post(ENTERPRISE_CONFIG_UPDATE, model);
    },
    async isFeatureEnabled(featureName) {
        return await instance.get(ENTERPRISE_CONFIG_IS_FEATURE_ENABLED + "?featureName=" + encodeURIComponent(featureName));
    },
    async getBrandingConfig() {
        return await instance.get(ENTERPRISE_CONFIG_GET_BRANDING);
    },
    // Stage 3.9 (Enterprise Candidate Experience Final UI Polish & UX Optimization) — anonymous read,
    // same shape as getBrandingConfig above. Consumed by modules/LanguageContext.js only.
    async getLanguageConfig() {
        return await instance.get(ENTERPRISE_CONFIG_GET_LANGUAGE);
    },

    // --- Governance & Approval Foundation (Sprint 2.2) ---
    async submitForApproval(model) {
        return await instance.post(GOVERNANCE_SUBMIT_FOR_APPROVAL, model);
    },
    async getPendingGovernanceRequests() {
        return await instance.get(GOVERNANCE_GET_PENDING_REQUESTS);
    },
    async approveGovernanceRequest(requestId) {
        return await instance.post(GOVERNANCE_APPROVE + "?requestId=" + requestId);
    },
    async rejectGovernanceRequest(requestId, comments) {
        return await instance.post(GOVERNANCE_REJECT + "?requestId=" + requestId, { Comments: comments });
    },
    async searchAuditLog(eventType, entityType, from, to, maxRows) {
        let query = "?maxRows=" + (maxRows || 200);
        if (eventType) query += "&eventType=" + encodeURIComponent(eventType);
        if (entityType) query += "&entityType=" + encodeURIComponent(entityType);
        if (from) query += "&from=" + encodeURIComponent(from);
        if (to) query += "&to=" + encodeURIComponent(to);
        return await instance.get(AUDIT_LOG_SEARCH + query);
    },

    // --- System Health, Diagnostics & Backup Awareness (Sprint 2.3) ---
    async getHealthStatus() {
        return await instance.get(SYSTEM_HEALTH_GET_STATUS);
    },
    async getDiagnostics() {
        return await instance.get(SYSTEM_HEALTH_GET_DIAGNOSTICS);
    },
    async getBackupStatus() {
        return await instance.get(SYSTEM_HEALTH_GET_BACKUP_STATUS);
    },
    async getRecentSecurityEvents(lines) {
        return await instance.get(SYSTEM_HEALTH_GET_RECENT_SECURITY_EVENTS + "?lines=" + (lines || 50));
    },

    // --- Operational Dashboard / Compliance Dashboard APIs (Sprint 2.3) ---
    async getOperationalMetrics() {
        return await instance.get(OPERATIONAL_DASHBOARD_GET_METRICS);
    },
    async getComplianceMetrics() {
        return await instance.get(COMPLIANCE_DASHBOARD_GET_METRICS);
    },

    // --- Enterprise Communication Foundation (Sprint 2.7.1) ---
    async getCommunicationHealth() {
        return await instance.get(COMMUNICATION_GET_HEALTH);
    },
    async testEmail(toEmail) {
        return await instance.post(COMMUNICATION_TEST_EMAIL, { ToEmail: toEmail });
    },
    async testSms(mobileNumber) {
        return await instance.post(COMMUNICATION_TEST_SMS, { MobileNumber: mobileNumber });
    },
    // Sprint 2.7.2 (Enterprise Communication Engine) — Part 10: counts only, no analytics.
    async getQueueSummary() {
        return await instance.get(COMMUNICATION_GET_QUEUE_SUMMARY);
    }
};

export default _EnterpriseOpsService;
